import { html, nothing, render } from "lit-html";
import { createRef, ref } from "lit-html/directives/ref.js";
import "./SearchInput";
import "./travel-data/CalendarInput";
import "./travel-data/GuestSelectorInput";
import {
	removeInitialContent,
	stateful,
} from "@escapio/shared/components/element-builder";
import {
	travelData,
	updateDates,
	onChangeDates,
	updateRooms,
	onChangeGuests,
	updateDatesAndRooms,
} from "../state/travelData";
import { translate } from "@escapio/shared/l10n/translate";
import { getExperimentVersion } from "../experiments/info";
import {
	addLayer,
	removeLayer,
} from "@escapio/shared/components/dialogs/StackManager";
import { objectsEqual } from "@escapio/shared/util/object";
import { TYPE_ALL_RESULTS } from "./SearchInput";

const isV1 = getExperimentVersion("exp27") == "v1";

customElements.define(
	"esc-search-panel-inputs",
	stateful(
		class extends removeInitialContent(HTMLElement) {
			static getInitialState() {
				return {
					lockedDestination: null,
					arrival: travelData.arrival,
					departure: travelData.departure,
					rooms: travelData.rooms,
				};
			}
			get queryValue() {
				return this.getAttribute("query-value");
			}

			get searchInspiration() {
				return this.getAttribute("search-inspiration");
			}

			get destinationOptional() {
				return this.hasAttribute("destination-optional");
			}

			connectedCallback() {
				this.searchRef = createRef();
				this.calendarRef = createRef();
				this.guestSelectorRef = createRef();
				onChangeDates((arrival, departure) => {
					this.arrival = arrival;
					this.departure = departure;
				});
				onChangeGuests(rooms => {
					this.rooms = rooms;
				});
			}

			render() {
				const isChanged =
					isV1 &&
					((this.lockedDestination &&
						this.lockedDestination.link != window.location.pathname) ||
						!objectsEqual(this.arrival, travelData.arrival) ||
						!objectsEqual(this.departure, travelData.departure) ||
						!objectsEqual(this.rooms, travelData.rooms));

				const isReadyToConfirm =
					isChanged &&
					(this.destinationOptional ||
						this.lockedDestination ||
						this.queryValue);

				render(
					html`<esc-search-input
							.value="${this.lockedDestination
								? this.stripTags(this.lockedDestination.label)
								: this.queryValue}"
							search-inspiration="${this.searchInspiration}"
							@lockEntry=${this.lockDestination}
							@open=${isV1 && this.onSearchInputOpen}
							${ref(this.searchRef)}
						></esc-search-input>
						<esc-calendar-input
							.arrival=${this.arrival}
							.departure=${this.departure}
							@confirm=${this.confirmDates}
							?disable-click-handler=${isV1}
							${ref(this.calendarRef)}
							@click=${isV1 && this.openCalendar}
						></esc-calendar-input>
						<esc-guest-selector-input
							.rooms=${this.rooms}
							@confirm=${this.confirmRooms}
							?disable-click-handler=${isV1}
							${ref(this.guestSelectorRef)}
							@click=${isV1 && this.openGuestSelector}
						></esc-guest-selector-input
						>${isV1
							? html`<button
									type="button"
									class="search-panel__button action-button"
									?disabled=${!isReadyToConfirm}
									@click=${this.confirmChanges}
								>
									<span class="fa fa-search fa--space-after"></span
									><span>${translate("basic_search")}</span>
								</button>`
							: nothing}`,
					this,
					{ host: this },
				);
			}

			lockDestination(evt) {
				if (isV1) {
					this.lockedDestination = evt.detail;
					if (this.lockedDestination?.type == TYPE_ALL_RESULTS) {
						this.confirmChanges(evt);
						return;
					}
					if (!this.queryValue && !this.arrival && !this.departure) {
						this.openCalendar();
					}
				} else {
					window.location = evt.detail.link;
				}
			}

			onSearchInputOpen() {
				this.startEditing();
				this.calendarRef.value.hideDialog();
				this.guestSelectorRef.value.hideDialog();
			}

			openCalendar() {
				this.startEditing();
				this.guestSelectorRef.value.hideDialog();
				this.calendarRef.value.showDialog(this.style.zIndex);
			}

			openGuestSelector() {
				this.startEditing();
				this.calendarRef.value.hideDialog();
				this.guestSelectorRef.value.showDialog(this.style.zIndex);
			}

			confirmDates({ detail }) {
				if (isV1) {
					this.arrival = detail.arrival;
					this.departure = detail.departure;
				} else {
					updateDates(detail.arrival, detail.departure);
				}
			}

			confirmRooms({ detail }) {
				if (isV1) {
					this.rooms = detail.rooms;
				} else {
					updateRooms(detail.rooms);
				}
			}

			confirmChanges(evt) {
				evt.preventDefault();
				evt.stopPropagation();
				updateDatesAndRooms(
					this.arrival,
					this.departure,
					this.rooms,
					!this.lockedDestination,
				);
				if (this.lockedDestination) {
					window.location = this.lockedDestination.link;
				}
				this.endEditing();
			}

			reset() {
				this.arrival = travelData.arrival;
				this.departure = travelData.departure;
				this.rooms = travelData.rooms;
				this.lockedDestination = null;
			}

			startEditing() {
				addLayer(
					this,
					() => {
						this.endEditing();
						this.reset();
					},
					false,
				);
			}

			endEditing() {
				this.calendarRef.value.hideDialog();
				this.guestSelectorRef.value.hideDialog();
				removeLayer(this);
			}

			stripTags(html) {
				const tmp = document.createElement("div");
				tmp.innerHTML = html;
				return tmp.textContent;
			}
		},
	),
);
